import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  TextField,
  Paper,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  CircularProgress,
  Typography,
  InputAdornment,
  Chip,
  Grid,
  Card,
  CardContent,
} from '@mui/material';
import {
  Search as SearchIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  AttachMoney as MoneyIcon,
  Task as TaskIcon,
  Description as DocumentIcon,
  Help as HelpIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { baseClient } from '../api/baseClient';

interface SearchResult {
  id: string;
  name: string;
  type: string;
  subtitle?: string;
}

const SearchPage: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // Icon mapping
  const getIcon = (type: string) => {
    const icons: Record<string, React.ReactElement> = {
      Account: <BusinessIcon />,
      Contact: <PersonIcon />,
      Deal: <MoneyIcon />,
      Task: <TaskIcon />,
      Document: <DocumentIcon />,
      Help: <HelpIcon />,
    };
    return icons[type] || <SearchIcon />;
  };

  // Color mapping
  const getColor = (type: string): "primary" | "success" | "warning" | "info" | "secondary" | "error" | "default" => {
    const colors: Record<string, "primary" | "success" | "warning" | "info" | "secondary" | "error"> = {
      Account: 'primary',
      Contact: 'success',
      Deal: 'warning',
      Task: 'info',
      Document: 'secondary',
      Help: 'error',
    };
    return colors[type] || 'default';
  };

  // Search function
  const performSearch = async (searchQuery: string) => {
    if (!searchQuery || searchQuery.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      console.log('🔍 Searching for:', searchQuery);
      const response = await baseClient.get<SearchResult[]>('/search', {
        params: { q: searchQuery, limit: 50 }
      });
      console.log('✅ Search results:', response);
      setResults(Array.isArray(response) ? response : []);
    } catch (error) {
      console.error('❌ Search error:', error);
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  // Debounced search
  useEffect(() => {
    const timeout = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timeout);
  }, [query]);

  // Handle result selection
  const handleSelectResult = (result: SearchResult) => {
    const routes: Record<string, string> = {
      Account: `/accounts/${result.id}`,
      Contact: `/contacts/${result.id}`,
      Deal: `/deals/${result.id}`,
      Task: '/tasks',
      Document: '/documents',
      Help: '/help',
    };
    navigate(routes[result.type] || '/');
  };

  // Group results by type
  const groupedResults = results.reduce((acc, result) => {
    if (!acc[result.type]) {
      acc[result.type] = [];
    }
    acc[result.type].push(result);
    return acc;
  }, {} as Record<string, SearchResult[]>);

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Search CRM
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Search across all your accounts, contacts, deals, tasks, and documents
        </Typography>
      </Box>

      {/* Search Input */}
      <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
        <TextField
          fullWidth
          size="medium"
          placeholder="Search for anything..."
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="large" />
              </InputAdornment>
            ),
            endAdornment: loading ? (
              <InputAdornment position="end">
                <CircularProgress size={24} />
              </InputAdornment>
            ) : null,
          }}
          sx={{
            '& .MuiInputBase-root': {
              fontSize: '1.2rem',
            },
          }}
        />
      </Paper>

      {/* Results */}
      {query.length >= 2 && (
        <Box>
          {loading ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
                Searching...
              </Typography>
            </Box>
          ) : results.length > 0 ? (
            <>
              <Box sx={{ mb: 3 }}>
                <Typography variant="h6" gutterBottom>
                  Found {results.length} result{results.length !== 1 ? 's' : ''}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                  {Object.entries(groupedResults).map(([type, items]) => (
                    <Chip
                      key={type}
                      label={`${type}: ${items.length}`}
                      color={getColor(type)}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>

              <Grid container spacing={2}>
                {Object.entries(groupedResults).map(([type, items]) => (
                  <Grid item xs={12} key={type}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {getIcon(type)}
                          {type}s ({items.length})
                        </Typography>
                        <List>
                          {items.map((result) => (
                            <ListItem key={result.id} disablePadding>
                              <ListItemButton onClick={() => handleSelectResult(result)}>
                                <ListItemIcon>
                                  {getIcon(result.type)}
                                </ListItemIcon>
                                <ListItemText
                                  primary={result.name}
                                  secondary={result.subtitle}
                                />
                                <Chip
                                  label={result.type}
                                  size="small"
                                  color={getColor(result.type)}
                                />
                              </ListItemButton>
                            </ListItem>
                          ))}
                        </List>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </>
          ) : (
            <Paper sx={{ p: 8, textAlign: 'center' }}>
              <SearchIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
              <Typography variant="h6" gutterBottom>
                No results found for "{query}"
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Try searching for:
              </Typography>
              <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap' }}>
                <Chip label="Deals" onClick={() => setQuery('deal')} />
                <Chip label="Contacts" onClick={() => setQuery('contact')} />
                <Chip label="Accounts" onClick={() => setQuery('account')} />
                <Chip label="Tasks" onClick={() => setQuery('task')} />
              </Box>
            </Paper>
          )}
        </Box>
      )}

      {query.length < 2 && (
        <Paper sx={{ p: 8, textAlign: 'center' }}>
          <SearchIcon sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            Start typing to search
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Enter at least 2 characters to see results
          </Typography>
        </Paper>
      )}
    </Container>
  );
};

export default SearchPage;
